import type { LiveComponent } from '@use-gpu/live';
import type { Blending, OffscreenTarget, UseGPURenderContext, VectorLike } from '@use-gpu/core';

import { makeBlendState } from '@use-gpu/core';
import { yeet, useMemo } from '@use-gpu/live';
import { chainTo } from '@use-gpu/shader/wgsl';

import { usePassContext } from '../../providers/pass-provider';

import { useTextureAccess } from '../../hooks/useTextureAccess';
import { useShader } from '../../hooks/useShader';
import { useShaderRef } from '../../hooks/useShaderRef';

import { useRenderContext } from '../../providers/render-provider';

import { useCopySample } from '../copy/value-copy';

import { getOutlineSample } from '@use-gpu/wgsl/outline/outline-sample.wgsl';
import { getOutlineResolve } from '@use-gpu/wgsl/outline/outline-resolve.wgsl';

import { octaToNormal16 } from '@use-gpu/wgsl/codec/normal16.wgsl';

export type OutlineDispatchProps = {
  bindPass?: (r: GPURenderPassEncoder) => void,
  globalLayout?: GPUBindGroupLayout,

  mode: 'edge' | 'resolve',
  facets: boolean,

  inner: number,
  outer: number,
  color: VectorLike,
  blend: Blending,

  depthRamp: number,
  normalRamp: number,

  targetContext: UseGPURenderContext,
  descriptor: GPURenderPassDescriptor,
};

export const OutlineDispatch: LiveComponent<OutlineDispatchProps> = (props: OutlineDispatchProps) => {
  const {
    bindPass,
    globalLayout,

    mode, // static
    facets,

    inner,
    outer,
    color,
    blend,

    depthRamp,
    normalRamp,

    targetContext,
    descriptor,
  } = props;

  const {
    targets: {normal, outline},
    views: { pre: { uniforms: { overscanMatrix }}},
  } = usePassContext();

  const {samples: msaaSamples, pixelRatio} = useRenderContext();
  const [normalContext] = normal;

  const [edgeTarget] = outline as OffscreenTarget[];

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const normalContextDepth = normalContext.depth!;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const normalContextSource = normalContext.source!;

  const overscanSize = useShaderRef([normalContext.width, normalContext.height]);
  const targetSize = useShaderRef([edgeTarget.width, edgeTarget.height]);

  const m = overscanMatrix?.current;
  const overscanScale = useShaderRef([m?.[0] ?? 1, m?.[5] ?? 1]);

  const innerRadius = useShaderRef(inner * pixelRatio);
  const outerRadius = useShaderRef(outer * pixelRatio);
  const outlineColor = useShaderRef(color);

  const defs = useMemo(() => ({
    DEPTH_RAMP: depthRamp,
    NORMAL_RAMP: normalRamp,
    MSAA_SAMPLES: msaaSamples,
    HAS_FACET: !!facets,
  }), [depthRamp, normalRamp, msaaSamples, facets]);

  type Draw = (r: GPURenderPassEncoder) => void;
  let draw: Draw | null = null;
  if (mode === 'edge') {
    const loadSourceDepth = useTextureAccess(normalContextDepth, 'u32').shader;

    const {format} = normalContextSource;
    const loadSourceNormal = useTextureAccess(normalContextSource, 'u32').shader;
    const loadSourceNormal16 = useMemo(() => {
      if (format.match(/uint/)) return loadSourceNormal;
      return chainTo(loadSourceNormal, octaToNormal16);
    }, [format, loadSourceNormal]);

    const getSample = useShader(getOutlineSample, [
      loadSourceNormal16,
      loadSourceDepth,
      overscanSize,
      overscanScale,
    ], defs);

    draw = useCopySample(targetContext, getSample, globalLayout);
  }
  if (mode === 'resolve') {
    const loadEdge = useTextureAccess(edgeTarget.source);

    const getSample = useShader(getOutlineResolve, [
      loadEdge,
      targetSize,
      innerRadius,
      outerRadius,
      outlineColor,
    ]);

    const blendState = makeBlendState(blend);
    draw = useCopySample(targetContext, getSample, globalLayout, blendState);
  }

  if (!draw) return null;

  const command = (commandEncoder: GPUCommandEncoder) => {
    targetContext.swap?.();

    const passEncoder = commandEncoder.beginRenderPass(descriptor);
    bindPass?.(passEncoder);
    draw?.(passEncoder);
    passEncoder.end();
  };

  return yeet({ outline: command });
};
